import { mapGetters } from 'vuex';
import { normalizeErrorMessage } from '@/utils/error-message';

export default {
  name: 'CourseCompletionMitra',
  data() {
    return {
      selectedCourse: null,
      currentPage: 1,
      perPage: 12,
      filters: {
        search: '',
        status: null,
        type: null
      },
      statusOptions: [
        { value: 'enrolled', label: 'Enrolled' },
        { value: 'in_review', label: 'In Review' },
        { value: 'completed', label: 'Completed' },
        { value: 'rejected', label: 'Rejected' }
      ],
      typeOptions: [
        { value: 'internal', label: 'Internal' },
        { value: 'external', label: 'External' },
        { value: 'external_lms', label: 'External LMS' }
      ],
      selectedPeriod: null,
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV
    };
  },
  computed: {
    ...mapGetters('courseCompletionMitra', ['courses', 'pagination', 'loading', 'error', 'summary', 'summaryLoading']),
    ...mapGetters('idpPeriod', ['IdpPeriod']),
    
    // Period dropdown options from store
    periodOptions() {
      const data = this.IdpPeriod;

      const list = data?.data;
      if (!list || !list.length) return [];

      return list.map(p => ({
        label: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id
      }));
    },
    
    isLoading() {
      return this.loading;
    },
    
    paginatedCourses() {
      return this.courses || [];
    },
    
    totalItems() {
      return this.pagination?.total || 0;
    },
    
    totalPages() {
      return this.pagination?.totalPage || 0;
    },
    
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages;
    }
  },
  
  watch: {
    'filters.search'() {
      this.currentPage = 1;
      this.debouncedFetchCourses();
    },
    'filters.status'() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    'filters.type'() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    selectedPeriod() {
      this.fetchSummary();
      this.fetchCourses();
    },
    currentPage() {
      this.fetchCourses();
    }
  },
  
  methods: {
    async fetchCourses() {
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage
        };
        
        if (this.filters.search) {
          params.search = this.filters.search;
        }
        
        if (this.filters.status) {
          params.status = this.filters.status;
        }
        
        if (this.filters.type) {
          params.type = this.filters.type;
        }
        
        if (this.selectedPeriod) {
          params.idp_period = this.selectedPeriod;
        }
        
        await this.$store.dispatch('courseCompletionMitra/getCourses', params);
      } catch (error) {
        console.error('Error fetching courses:', error);
        this.$swal({
          title: 'Error',
          text: normalizeErrorMessage(error.message || 'Daftar pelatihan belum berhasil dimuat'),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    debouncedFetchCourses() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchCourses();
      }, 500);
    },
    
    async fetchSummary() {
      if (!this.selectedPeriod) return;
      
      try {
        const params = {
          idp_period: this.selectedPeriod
        };
        await this.$store.dispatch('courseCompletionMitra/getSummary', params);
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    },
    
    viewCourseDetail(course) {
      this.selectedCourse = course;
      this.$bvModal.show('modal-course-detail-completion');
    },
    
    openUploadModal(course) {
      this.selectedCourse = course;
      this.$bvModal.show('modal-upload-certificate');
    },
    
    async downloadCertificate(course) {
      try {
        const response = await this.$store.dispatch('courseCompletionMitra/downloadCertificate', course.id);
        
        // Download as blob (expects .zip file from API)
        if (response && response instanceof Blob) {
          const url = window.URL.createObjectURL(response);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `certificate-${course.title || course.id}.zip`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else if (response.certificate_url) {
          // Fallback: open URL if available
          window.open(response.certificate_url, '_blank');
        } else if (course.certificate_path) {
          // Fallback: open certificate path
          window.open(`${this.imageBaseUrl}/attachments/${course.certificate_path}`, '_blank');
        }
        
        this.$swal({
          title: 'Success',
          text: 'Sertifikat berhasil didownload.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error downloading certificate:', error);
        this.$swal({
          title: 'Error',
          text: 'Sertifikat belum berhasil didownload.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    },
    
    handleCertificateUploaded() {
      this.fetchCourses();
      this.fetchSummary();
      this.$bvModal.hide('modal-upload-certificate');
    },
    
    changePage(page) {
      if (page === '...' || page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    
    clearFilters() {
      this.filters = {
        search: '',
        status: null,
        type: null
      };
      this.currentPage = 1;
    },
    
    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },
    
    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type === 'internal') {
        return 'fe fe-home';
      } else if (type === 'external') {
        return 'fe fe-globe';
      } else if (type === 'external_lms') {
        return 'fe fe-monitor';
      }
      return 'fe fe-book-open';
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        'available': 'badge bg-primary',
        'in_review': 'badge bg-warning',
        'approved': 'badge bg-success',
        'rejected': 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    },
    
    getTypeBadgeClass(type) {
      const classes = {
        'internal': 'badge bg-info',
        'external': 'badge bg-purple',
        'external_lms': 'badge bg-teal'
      };
      return classes[type] || 'badge bg-secondary';
    },
    
    formatStatus(status) {
      const statusMap = {
        'enrolled': 'Enrolled',
        'in_review': 'In Review',
        'completed': 'Completed',
        'rejected': 'Rejected'
      };
      return statusMap[status] || status;
    },
    
    formatType(type) {
      const typeMap = {
        'internal': 'Internal',
        'external': 'External',
        'external_lms': 'External LMS'
      };
      return typeMap[type] || type;
    },
    
    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return '';
      
      const fmt = (dateStr) => {
        const d = new Date(dateStr);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      };
      
      return `${fmt(startDate)} - ${fmt(endDate)}`;
    }
  },
  
  async mounted() {
    console.log('✅ Course Completion Mitra page mounted');
    
    // Fetch TNA periods first
    try {
      const result = await this.$store.dispatch('idpPeriod/getIdpPeriod');
      
      // Auto-select period with vstatus = 'open'
      const list = result?.data ?? [];
      if (list.length > 0) {
        const openPeriod = list.find(p => p.vstatus === 'open');
        if (openPeriod) {
          this.selectedPeriod = openPeriod.id;
        } else if (this.periodOptions.length > 0) {
          // Fallback to first period if no open period found
          this.selectedPeriod = this.periodOptions[0].value;
        }
      }
    } catch (error) {
      console.error('Error fetching periods:', error);
    }
    
    // Fetch courses and summary
    this.fetchCourses();
    this.fetchSummary();
  },
  
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
};
